import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatAUD(amount) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount)
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

// Strip the raw code value from denomination code redemption descriptions so it
// is never visible to members in their transaction history. Applied at render
// time only — the database value is preserved for admin investigation.
//
// "Denomination code SHW-20 ($20) redeemed — ..." → "Denomination code ($20) redeemed — ..."
//
// The regex matches the code token (non-whitespace, between "code " and " ($") and
// removes it. Descriptions that do not match this pattern are returned unchanged.
function maskRedemptionCode(desc) {
  if (!desc) return desc
  return desc.replace(/^(Denomination code\s+)\S+(\s+\(\$)/, '$1$2')
}

function timeUntil(iso) {
  const diff  = new Date(iso) - new Date()
  if (diff <= 0) return { label: 'Expired', cls: 'expiry-urgent' }
  const hours = Math.floor(diff / 3600000)
  const mins  = Math.floor((diff % 3600000) / 60000)
  if (hours < 1)  return { label: `${mins}m remaining`, cls: 'expiry-urgent' }
  if (hours < 4)  return { label: `${hours}h ${mins}m remaining`, cls: 'expiry-soon' }
  return { label: `${hours}h ${mins}m remaining`, cls: 'expiry-ok' }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function BalanceHero({ member }) {
  const [dollars, cents] = formatAUD(member.credit_balance).split('.')

  return (
    <div className="balance-hero">
      <div>
        <p className="balance-label">Available Credit</p>
        <p className="balance-amount">
          {dollars}
          <span className="balance-cents">.{cents}</span>
        </p>
        <p className="text-sm" style={{ opacity: 0.55, marginTop: '0.5rem' }}>
          {member.name}
        </p>
      </div>
      <div style={{ textAlign: 'right' }}>
        {/* membership_status is vestigial (always 'active' in practice) — status is the single source of truth for lifecycle state */}
        {(() => {
          const BADGE = {
            active:    { cls: 'active',    label: 'ACTIVE' },
            lapsed:    { cls: 'lapsed',    label: 'EXPIRING' },
            closed:    { cls: 'closed',    label: 'CLOSED' },
            suspended: { cls: 'suspended', label: 'SUSPENDED' },
          }
          const b = BADGE[member.status] || { cls: 'inactive', label: member.status }
          return <span className={`status-badge dot ${b.cls}`}>{b.label}</span>
        })()}
        <p className="text-sm" style={{ opacity: 0.45, marginTop: '0.5rem', fontSize: '0.72rem' }}>
          Member since {new Date(member.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
        </p>
      </div>
    </div>
  )
}

function UseCreditModal({ member, onClose, onSuccess }) {
  const [amount,  setAmount]  = useState('')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)
  const [result,  setResult]  = useState(null) // { code, expires_at, amount }
  const [copied,  setCopied]  = useState(false)

  const MAX_DENOM = 239
  const maxWhole  = Math.min(Math.floor(Number(member.credit_balance)), MAX_DENOM)

  async function handleGenerate(e) {
    e.preventDefault()
    setError(null)
    const amt = Math.floor(parseFloat(amount))
    if (!amt || amt < 1)        { setError('Please enter a whole dollar amount of at least $1.'); return }
    if (amt > Number(member.credit_balance)) { setError(`Amount exceeds your balance of ${formatAUD(Number(member.credit_balance))}.`); return }
    if (amt > MAX_DENOM)        { setError(`Maximum code amount is $${MAX_DENOM}.`); return }

    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/generate-code', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ amount: amt })  // already floored above
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Failed to generate code.')
      setResult(json)
      onSuccess()
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  function handleCopy() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(result.code).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }).catch(() => fallbackCopy())
    } else {
      fallbackCopy()
    }
  }

  function fallbackCopy() {
    // execCommand fallback for older/restricted browsers
    const el = document.createElement('textarea')
    el.value = result.code
    el.style.position = 'fixed'
    el.style.opacity  = '0'
    document.body.appendChild(el)
    el.select()
    try {
      document.execCommand('copy')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* silently fail */ }
    document.body.removeChild(el)
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal">
        {!result ? (
          <>
            <h2 className="modal-title">Use Credit</h2>
            <p className="text-sm text-muted" style={{ marginBottom: '1.25rem' }}>
              Generate a one-time discount code to use at checkout. The code is valid for
              24 hours and is locked to your account email.
            </p>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleGenerate}>
              <div className="form-group">
                <label className="form-label">Amount (whole dollars only)</label>
                <input
                  type="number"
                  className="form-input"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  min="1"
                  max={maxWhole}
                  step="1"
                  placeholder={`$1 – $${maxWhole}`}
                  required
                />
                <p className="form-hint">
                  Available balance: {formatAUD(Number(member.credit_balance))} — codes are issued in whole dollar amounts
                </p>
              </div>

              <div className="alert alert-info" style={{ fontSize: '0.8rem' }}>
                Credit is only deducted when the code is successfully used at checkout.
                Unused codes expire after 24 hours.
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Generating…' : 'Generate Code'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="modal-title">Your Discount Code</h2>

            <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
              Code generated successfully! Enter it at checkout.
            </div>

            <div
              className="code-display"
              onClick={handleCopy}
              title="Click to copy"
              style={{
                marginBottom: '0.5rem',
                cursor:       'pointer',
                userSelect:   'none',
                transition:   'opacity 0.15s',
                opacity:      copied ? 0.75 : 1,
              }}
            >
              {result.code}
            </div>

            <div style={{ textAlign: 'center', height: '1.5rem', marginBottom: '0.75rem' }}>
              {copied
                ? <span style={{ color: '#2d7a3a', fontWeight: 600, fontSize: '0.875rem' }}>✓ Copied to clipboard!</span>
                : <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Click code to copy</span>
              }
            </div>

            <div className="text-sm text-muted" style={{ lineHeight: 1.7 }}>
              <p><strong>Amount:</strong> {formatAUD(result.amount)}</p>
              <p><strong>Expires:</strong> {formatDate(result.expires_at)}</p>
              <p style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                This code is locked to your email address and can only be used on orders
                containing members-only products. It cannot be transferred.
              </p>
            </div>

            <div className="modal-footer">
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function PendingCodes({ codes, onRefresh }) {
  const pending = codes.filter(c => c.status === 'pending')
  if (pending.length === 0) return null

  return (
    <div className="card">
      <p className="card-title">Active Codes</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Amount</th>
              <th>Expires</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pending.map(code => {
              const exp    = timeUntil(code.expires_at)
              // isLive: expires_at has not yet passed. Even if DB status is still
              // 'pending', hide the code value once the clock has run out — the
              // next poll will mark it expired, but until then the window is ≤15 min.
              const isLive = new Date(code.expires_at) > new Date()
              return (
                <tr key={code.id}>
                  <td>
                    {isLive
                      ? <span className="code-inline">{code.code}</span>
                      : <span style={{ color: 'var(--muted)', fontStyle: 'italic', fontSize: '0.8rem' }}>Expired</span>
                    }
                  </td>
                  <td className="amount-neutral">{formatAUD(code.amount)}</td>
                  <td className={exp.cls}>{exp.label}</td>
                  <td><span className="type-badge earned">Pending</span></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TransactionHistory({ transactions }) {
  if (transactions.length === 0) {
    return (
      <div className="card">
        <p className="card-title">Transaction History</p>
        <div className="empty-state">No transactions yet.</div>
      </div>
    )
  }

  return (
    <div className="card">
      <p className="card-title">Transaction History</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Type</th>
              <th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Human-readable labels for known transaction types.
              // Unknown types fall back to the raw type string (neutral badge).
              const TYPE_LABELS = {
                earned:   'Earned',
                used:     'Redeemed',
                expired:  'Expired',
                returned: 'Returned',
              }
              // Debit types: rendered in default colour (no red, no "+" prefix).
              const NEUTRAL_TYPES = new Set(['used', 'expired'])

              return transactions.map(tx => {
                const label   = TYPE_LABELS[tx.type] || tx.type
                const neutral = NEUTRAL_TYPES.has(tx.type)
                const isPos   = tx.amount > 0
                const isNeg   = tx.amount < 0
                const amtCls  = neutral   ? 'amount-neutral'
                              : isPos     ? 'amount-positive'
                              : isNeg     ? 'amount-negative'
                              : 'amount-neutral'
                const prefix  = (!neutral && isPos) ? '+' : ''
                return (
                  <tr key={tx.id}>
                    <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(tx.created_at)}
                    </td>
                    <td>{maskRedemptionCode(tx.description) || '—'}</td>
                    <td><span className={`type-badge ${tx.type}`}>{label}</span></td>
                    <td className={`text-right ${amtCls}`}>
                      {tx.amount === 0 ? '—' : `${prefix}${formatAUD(Math.abs(tx.amount))}`}
                    </td>
                  </tr>
                )
              })
            })()}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function CodeHistory({ codes }) {
  const history = codes.filter(c => c.status !== 'pending')
  if (history.length === 0) return null

  return (
    <div className="card">
      <p className="card-title">Code History</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {history.map(code => (
              <tr key={code.id}>
                <td>{formatAUD(code.amount)}</td>
                <td><span className={`type-badge ${code.status}`}>{code.status.replace('_', ' ')}</span></td>
                <td className="text-sm text-muted">
                  {code.redeemed_at
                    ? formatDate(code.redeemed_at)
                    : formatDate(code.created_at)}
                </td>
                <td className="text-sm text-muted">
                  {code.order_id ? `Order #${code.order_id}` : '—'}
                  {code.attempted_email && (
                    <span style={{ color: 'var(--danger)' }}> · {code.attempted_email}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MemberDashboard({ member: initialMember, onMemberUpdate }) {
  const [member,       setMember]       = useState(initialMember)
  const [transactions, setTransactions] = useState([])
  const [codes,        setCodes]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [showModal,    setShowModal]    = useState(false)

  const fetchData = useCallback(async () => {
    const [txRes, codeRes, memberRes] = await Promise.all([
      supabase
        .from('credit_transactions')
        .select('*')
        .eq('member_id', member.id)
        .neq('type', 'flagged')          // flagged rows belong in admin Fraud Alerts, not member view
        .order('created_at', { ascending: false })
        .limit(100),
      supabase
        .from('discount_codes')
        .select('*')
        .eq('member_id', member.id)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('members')
        .select('*')
        .eq('id', member.id)
        .single()
    ])

    if (txRes.data)     setTransactions(txRes.data)
    if (codeRes.data)   setCodes(codeRes.data)
    if (memberRes.data) {
      setMember(memberRes.data)
      onMemberUpdate(memberRes.data)
    }
    setLoading(false)
  }, [member.id, onMemberUpdate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Refresh pending codes every 60s to update expiry countdowns
  useEffect(() => {
    const interval = setInterval(() => {
      const hasPending = codes.some(c => c.status === 'pending')
      if (hasPending) fetchData()
    }, 60000)
    return () => clearInterval(interval)
  }, [codes, fetchData])

  async function handleSignOut() {
    await supabase.auth.signOut()
  }

  return (
    <div className="layout">
      <header className="topbar">
        <div className="topbar-brand">
          Shepherd's Hut Wines
          <span>Flock Credit</span>
        </div>
        <div className="topbar-actions">
          <span className="topbar-user">{member.email}</span>
          <button className="btn btn-secondary btn-sm" style={{ borderColor: 'rgba(234,230,219,0.25)', color: 'var(--cream)' }} onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </header>

      <main className="main-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            <BalanceHero member={member} />

            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowModal(true)}
                disabled={
                  member.credit_balance <= 0 ||
                  member.status !== 'active'
                }
                title={
                  member.status !== 'active'
                    ? 'Subscription inactive — contact info@shepherdshutwines.com.au'
                    : undefined
                }
              >
                Use Credit
              </button>
              <button className="btn btn-secondary" onClick={fetchData}>
                Refresh
              </button>
            </div>

            {member.status === 'lapsed' && (
              <div style={{
                background:   '#FEF3C7',
                border:       '1px solid #D97706',
                borderRadius: 6,
                padding:      '1rem 1.25rem',
                marginTop:    '1rem',
                color:        '#92400E',
                fontSize:     '0.875rem',
                lineHeight:   1.7,
              }}>
                <strong style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.95rem' }}>
                  Subscription no longer active
                </strong>
                Your Flock subscription is no longer active. Your credit balance is still available to use until 65 days after your last payment — contact us at{' '}
                <a href="mailto:info@shepherdshutwines.com.au" style={{ color: '#92400E', fontWeight: 600 }}>
                  info@shepherdshutwines.com.au
                </a>{' '}
                to arrange a one-off discount code. If you think there's been an error, please get in touch so we can follow it up.
              </div>
            )}

            {member.status === 'closed' && (
              <div style={{
                background:   '#F3F4F6',
                border:       '1px solid #9CA3AF',
                borderRadius: 6,
                padding:      '1rem 1.25rem',
                marginTop:    '1rem',
                color:        '#374151',
                fontSize:     '0.875rem',
                lineHeight:   1.7,
              }}>
                <strong style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.95rem' }}>
                  Account closed
                </strong>
                Your Flock account has been closed and any remaining credit has expired. If you'd like to rejoin,
                please contact us at{' '}
                <a href="mailto:info@shepherdshutwines.com.au" style={{ color: '#374151', fontWeight: 600 }}>
                  info@shepherdshutwines.com.au
                </a>{' '}
                or purchase a new Flock subscription.
              </div>
            )}

            {member.status === 'suspended' && (
              <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
                Your account is suspended. Contact Shepherd's Hut Wines to restore access.
              </div>
            )}

            <div style={{ marginTop: '1.25rem' }}>
              <PendingCodes codes={codes} onRefresh={fetchData} />
            </div>

            <div style={{ marginTop: '1.25rem' }}>
              <TransactionHistory transactions={transactions} />
            </div>

            <div style={{ marginTop: '1.25rem' }}>
              <CodeHistory codes={codes} />
            </div>
          </>
        )}
      </main>

      {showModal && (
        <UseCreditModal
          member={member}
          onClose={() => { setShowModal(false); fetchData() }}
          onSuccess={() => fetchData()}
        />
      )}
    </div>
  )
}
